/**
 * AgentVault "create" command — spin up a brand new OpenClaw peer agent.
 *
 * Ports the logic from ~/.openclaw/workspace/scripts/create-agent.sh into
 * TypeScript, eliminating the jq dependency.
 *
 * Usage: npx @agentvault/agentvault create <name> --token=av_tok_...
 */

import { execSync } from "node:child_process";
import { existsSync, mkdirSync, readFileSync, writeFileSync, copyFileSync } from "node:fs";
import { join } from "node:path";
import { createInterface } from "node:readline";
import { runSetupCommand } from "./setup.js";

export interface CreateAgentOptions {
  name: string;       // e.g. "nova"
  token: string;      // av_tok_...
  port?: number;      // auto-detected if omitted
  apiUrl: string;
  force?: boolean;
}

/* ── Helpers ──────────────────────────────────────────────────────────── */

/** Known alternative-framework keywords that suggest the user may have
 *  picked the wrong installer. action_item Task C: Eric ran this OpenClaw
 *  installer with name=hermes intending to install the Hermes plugin
 *  (a separate Python package). The CLI did exactly what it was told and
 *  created an OpenClaw agent named "hermes" in ~/.openclaw/. The fix is
 *  a soft-confirm hint pointing at the correct installer command. */
const ALT_FRAMEWORK_HINTS: Record<string, { name: string; install: string }> = {
  hermes: {
    name: "Hermes",
    install:
      "pip install agentvault-hermes && agentvault-hermes setup --token=<paste-the-token>",
  },
  nemo: {
    name: "NVIDIA NeMo",
    install:
      "(NeMo plugin coming soon — see https://github.com/motiveflowllc/agentvault-gen2 for roadmap)",
  },
};

/** Return alternative-framework hint when the agent name matches a
 *  known alt-framework keyword (case-insensitive, whitespace-trimmed).
 *  Returns null for ordinary names. Exported with underscore prefix to
 *  signal internal-helper status — not a public API. */
export function _checkAltFrameworkName(
  name: string,
): { name: string; install: string } | null {
  const lower = name.toLowerCase().trim();
  if (!lower) return null;
  return ALT_FRAMEWORK_HINTS[lower] ?? null;
}

/** Wait for the user to press Enter (or Ctrl-C to abort).
 *  Used by the alt-framework soft-confirm gate. */
async function _waitForEnter(): Promise<void> {
  return new Promise((resolve) => {
    const rl = createInterface({ input: process.stdin, output: process.stdout });
    rl.once("line", () => { rl.close(); resolve(); });
    rl.once("close", () => { resolve(); });
  });
}

/** Resolve the OpenClaw home directory (~/.openclaw) */
export function openclawHome(): string {
  const home = process.env.HOME ?? process.env.USERPROFILE ?? "";
  return join(home, ".openclaw");
}

/** Parse openclaw.json and return its contents. */
export function readOpenClawConfig(home: string): any {
  const configPath = join(home, "openclaw.json");
  const raw = readFileSync(configPath, "utf-8");
  return JSON.parse(raw);
}

/** Write openclaw.json with a backup. */
function writeOpenClawConfig(home: string, config: any, backupSuffix: string): void {
  const configPath = join(home, "openclaw.json");
  const backupPath = `${configPath}.bak.pre-${backupSuffix}`;
  copyFileSync(configPath, backupPath);
  writeFileSync(configPath, JSON.stringify(config, null, 2) + "\n", "utf-8");
}

/**
 * Collect all httpPort values from channels.agentvault.accounts in openclaw.json.
 * The real config structure is:
 *   { channels: { agentvault: { accounts: { <id>: { httpPort: N, ... } } } } }
 */
function collectPorts(config: any): number[] {
  const ports: number[] = [];
  const accounts = config?.channels?.agentvault?.accounts;
  if (accounts && typeof accounts === "object") {
    for (const acct of Object.values(accounts) as any[]) {
      if (typeof acct.httpPort === "number") {
        ports.push(acct.httpPort);
      }
    }
  }
  return ports;
}

/**
 * Scan all accounts in openclaw.json for httpPort values and return next free port.
 * Minimum port is `startPort` (default 18790).
 */
export function findNextPort(config: any, startPort = 18800): number {
  const ports = collectPorts(config);
  if (ports.length === 0) return startPort;
  return Math.max(...ports, startPort - 1) + 1;
}

/** Check if a specific port is already in use in the config. */
function isPortInUse(config: any, port: number): boolean {
  return collectPorts(config).includes(port);
}

/** Generate workspace template files for a new agent. */
export function generateWorkspaceFiles(name: string): Record<string, string> {
  const displayName = name.charAt(0).toUpperCase() + name.slice(1).toLowerCase();

  return {
    "IDENTITY.md": `# IDENTITY.md - Who Am I?

- **Name:** ${displayName}
- **Creature:** AI assistant — a capable peer agent in the AgentVault network
- **Vibe:** Direct, competent, focused on their specialty domain
- **Emoji:** (placeholder — update to something fitting)
- **Avatar:** _(none set yet)_

> TODO: Customize this file to give ${name} a distinct identity.
`,
    "SOUL.md": `# SOUL.md - Who You Are

> TODO: Define ${name}'s personality, tone, and behavioral rules.
> Copy and adapt from ~/.openclaw/workspace/SOUL.md as a starting point.

## Core Truths

- Be genuinely helpful, not performatively helpful.
- Have opinions. Be resourceful before asking.
- Earn trust through competence.

## Channel Reply Rules

- Never include reasoning or internal logic in visible replies.
- No narrating your approach before or after tool calls.
- Just deliver the answer — clean and direct.

## Vibe

_(Define ${name}'s specific personality here.)_
`,
    "HEARTBEAT.md": `# Heartbeat

- Stay available. If nothing needs attention, reply HEARTBEAT_OK.
- **Heartbeat replies are INTERNAL ONLY.** Reply with ONLY \`HEARTBEAT_OK\` when nothing needs attention.
- If something genuinely urgent needs attention, send it proactively FIRST, THEN reply HEARTBEAT_OK.
`,
    "MEMORY.md": `# MEMORY.md - Long-Term Memory

_(Empty — ${name} starts with a clean memory. Update as the agent learns.)_
`,
  };
}

/* ── Main Command ─────────────────────────────────────────────────────── */

export async function runCreateCommand(options: CreateAgentOptions): Promise<void> {
  const { name, token, apiUrl, force } = options;
  const home = openclawHome();
  const configPath = join(home, "openclaw.json");
  const workspaceDir = join(home, `workspace-${name}`);
  const dataDir = join(home, "agents", name, "agentvault-data");
  const templateDir = join(home, "workspace");

  // ── Soft-confirm: alt-framework name gate ─────────────────────────────
  const altHint = _checkAltFrameworkName(name);
  if (altHint && !force) {
    console.log(`
⚠️  Note: '${name}' is also the name of a different AgentVault framework.

This command creates an OpenClaw agent named '${name}' in ~/.openclaw/.
If you wanted the ${altHint.name} plugin (a different framework with its own
installer), exit (Ctrl-C) and run:

    ${altHint.install}

To continue with OpenClaw, press Enter.
`);
    await _waitForEnter();
  }

  console.log(`
╔══════════════════════════════════════════════╗
║       AgentVault — Create New Agent          ║
╚══════════════════════════════════════════════╝
`);

  // ── Step 1: Preflight ──────────────────────────────────────────────
  console.log("  Step 1/7 — Preflight checks...");

  // Extend PATH to find openclaw regardless of how npx was invoked
  const userHome = process.env.HOME ?? process.env.USERPROFILE ?? "";
  const extraPaths = [
    `${userHome}/.local/bin`,
    `${userHome}/.pnpm/bin`,
    `${userHome}/Library/pnpm/bin`,
    "/usr/local/bin",
    "/opt/homebrew/bin",
  ].join(":");
  const env = { ...process.env, PATH: `${extraPaths}:${process.env.PATH ?? ""}` };

  try {
    const result = execSync("openclaw --version", { stdio: "pipe", env });
    console.log(`  OpenClaw found: ${result?.toString().trim()}`);
  } catch {
    console.error("  Error: 'openclaw' not found in PATH. Is OpenClaw installed?");
    process.exit(1);
  }

  if (!existsSync(configPath)) {
    console.error(`  Error: openclaw.json not found at ${configPath}`);
    process.exit(1);
  }

  console.log("  Preflight passed.\n");

  // ── Step 2: Auto-detect port ───────────────────────────────────────
  console.log("  Step 2/7 — Detecting port...");

  const config = readOpenClawConfig(home);
  let port: number;

  if (options.port != null) {
    port = options.port;
    if (isPortInUse(config, port)) {
      console.error(`  Error: Port ${port} is already in use in openclaw.json.`);
      process.exit(1);
    }
    console.log(`  Using explicit port ${port}.\n`);
  } else {
    port = findNextPort(config);
    console.log(`  Auto-assigned port ${port}.\n`);
  }

  // ── Step 3: Create agent ───────────────────────────────────────────
  console.log("  Step 3/7 — Creating agent with OpenClaw...");

  try {
    // Use --non-interactive + --workspace to avoid interactive prompts (stdio: "pipe" suppresses them)
    execSync(`openclaw agents add --non-interactive --workspace "${workspaceDir}" -- ${name}`, { stdio: "pipe", env });
    console.log(`  Agent '${name}' created.\n`);
  } catch {
    console.log(`  Warning: 'openclaw agents add' failed — agent may already exist. Continuing.\n`);
  }

  // ── Step 4: Create directories ─────────────────────────────────────
  console.log("  Step 4/7 — Creating directories...");

  mkdirSync(workspaceDir, { recursive: true });
  mkdirSync(dataDir, { recursive: true });
  console.log(`  Workspace: ${workspaceDir}`);
  console.log(`  DataDir:   ${dataDir}\n`);

  // ── Step 5: Write workspace template files ─────────────────────────
  console.log("  Step 5/7 — Writing workspace files...");

  const files = generateWorkspaceFiles(name);
  for (const [filename, content] of Object.entries(files)) {
    writeFileSync(join(workspaceDir, filename), content, "utf-8");
  }

  // Copy AGENTS.md and USER.md from template workspace if they exist
  for (const copyFile of ["AGENTS.md", "USER.md"]) {
    const src = join(templateDir, copyFile);
    if (existsSync(src)) {
      copyFileSync(src, join(workspaceDir, copyFile));
      console.log(`  Copied ${copyFile} from template workspace.`);
    }
  }

  console.log("  Workspace files written.\n");

  // ── Step 6: Run enrollment ─────────────────────────────────────────
  console.log("  Step 6/7 — Enrolling with AgentVault...\n");

  await runSetupCommand({
    token,
    name,
    apiUrl,
    dataDir,
    accountId: name,
    force,
  });

  // ── Step 6b: Pull identity + seed knowledge from AgentVault ─────────
  // Architecture: workspace files = personality/vibe only. Skills/instructions
  // stay in backend hub_capabilities (fetched at runtime by plugin).
  // Knowledge goes into online semantic memory (recalled by _recallMemories).
  try {
    const stateFile = join(dataDir, "agentvault.json");
    if (existsSync(stateFile)) {
      const state = JSON.parse(readFileSync(stateFile, "utf-8"));
      const jwt = state.deviceJwt;
      const hubId = state.hubId || state.agentHubId;

      if (jwt && hubId) {
        console.log("  Pulling agent configuration from AgentVault...");

        // Fetch hub identity (lightweight — identity + description only)
        const hubRes = await fetch(`${apiUrl}/api/v1/hub/identities/${hubId}/config`, {
          headers: { Authorization: `Bearer ${jwt}` },
        });

        if (hubRes.ok) {
          const hub = await hubRes.json() as any;
          const displayName = hub.display_name || name;
          const description = hub.description || "";
          const capabilities = hub.capabilities || [];
          const skillCount = capabilities.filter((c: any) => c.skill_content).length;
          const toolCount = capabilities.length - skillCount;

          // Rewrite IDENTITY.md with hub identity
          const identityContent = `# IDENTITY.md - Who Am I?

- **Name:** ${displayName}
- **Creature:** AI assistant — a capable peer agent in the AgentVault network
- **Hub Address:** ${hub.hub_address || `${name}.agentvault.hub`}
- **Description:** ${description || "(not set yet)"}
- **Emoji:** (update to something fitting)
`;
          writeFileSync(join(workspaceDir, "IDENTITY.md"), identityContent, "utf-8");

          // Rewrite SOUL.md — personality/vibe only, NO skill content
          // Skills are fetched at runtime by the plugin from hub capabilities API
          const soulContent = `# SOUL.md - Who You Are

## Identity
You are **${displayName}**${description ? ` — ${description}` : ""}.

## Core Truths
- Be genuinely helpful, not performatively helpful.
- Have opinions. Be resourceful before asking.
- Earn trust through competence.

## Channel Reply Rules
- Never include reasoning or internal logic in visible replies.
- No narrating your approach before or after tool calls.
- Just deliver the answer — clean and direct.

## Skills & Knowledge
Your skills and knowledge are managed online through AgentVault.
${skillCount > 0 ? `You have ${skillCount} skill(s) registered.` : "No skills registered yet — configure via the Agent Builder."}
${toolCount > 0 ? `You have ${toolCount} tool(s) available.` : ""}
Skills are injected at runtime — no need to list them here.
`;
          writeFileSync(join(workspaceDir, "SOUL.md"), soulContent, "utf-8");

          // Seed initial knowledge into online semantic memory (if configured)
          // The Agent Builder Stage 5 (Contract) can set initial_knowledge entries
          // These get seeded here so the agent has context from day one
          try {
            const memRes = await fetch(`${apiUrl}/api/v1/memory/stats`, {
              headers: { Authorization: `Bearer ${jwt}` },
            });
            if (memRes.ok) {
              // Memory service is available — seed any initial knowledge from capabilities
              for (const cap of capabilities) {
                if (cap.skill_content && cap.description) {
                  try {
                    await fetch(`${apiUrl}/api/v1/memory/semantic`, {
                      method: "POST",
                      headers: { Authorization: `Bearer ${jwt}`, "Content-Type": "application/json" },
                      body: JSON.stringify({
                        text: `Skill: ${cap.capability_name}. ${cap.description}`,
                        category: "skill_knowledge",
                      }),
                    });
                  } catch { /* best effort */ }
                }
              }
              console.log(`  Seeded ${skillCount} skill descriptions into semantic memory.`);
            }
          } catch { /* memory service may not be available */ }

          console.log(`  Identity written. ${skillCount} skills + ${toolCount} tools registered in hub (runtime-injected).\n`);
        } else {
          console.log(`  Could not fetch hub config (${hubRes.status}) — using defaults.\n`);
        }
      }
    }
  } catch (pullErr) {
    console.log(`  Could not pull agent config — using defaults. (${pullErr})\n`);
  }

  // ── Step 7: Patch httpPort + bindings ────────────────────────────────
  console.log("\n  Step 7/7 — Patching openclaw.json...");

  try {
    const freshConfig = readOpenClawConfig(home);
    let patched = false;

    // Real config structure: channels.agentvault.accounts.<name>.httpPort
    const avChannel = freshConfig?.channels?.agentvault;
    if (avChannel?.accounts) {
      const acct = avChannel.accounts[name];
      if (acct) {
        acct.httpPort = port;
        patched = true;
      }
    }

    // Ensure channel binding exists for this account
    if (avChannel) {
      const channelBindings = avChannel.bindings as Array<{ match?: { accountId?: string } }> | undefined;
      const hasAccountBinding = channelBindings?.some(
        (b) => b.match?.accountId === name,
      );
      if (!hasAccountBinding) {
        // Insert before the wildcard if one exists, otherwise append
        const arr = channelBindings ?? [];
        const wildcardIdx = arr.findIndex((b) => b.match?.accountId === "*");
        const entry = { match: { accountId: name } };
        if (wildcardIdx >= 0) {
          arr.splice(wildcardIdx, 0, entry);
        } else {
          arr.push(entry);
        }
        avChannel.bindings = arr;
        patched = true;
        console.log(`  Added channel binding for account "${name}".`);
      }

      // Ensure a wildcard binding exists so OpenClaw doctor doesn't warn
      const hasWildcard = (avChannel.bindings as Array<{ match?: { accountId?: string } }>).some(
        (b) => b.match?.accountId === "*",
      );
      if (!hasWildcard) {
        (avChannel.bindings as any[]).push({ match: { accountId: "*" } });
        patched = true;
        console.log(`  Added wildcard channel binding (accountId: "*").`);
      }
    }

    // Ensure top-level binding exists so OpenClaw routes messages
    // for this account to the correct agent process
    const topBindings = freshConfig.bindings as Array<{ agentId?: string; match?: { channel?: string; accountId?: string } }> | undefined;
    const hasTopBinding = topBindings?.some(
      (b) => b.agentId === name && b.match?.accountId === name,
    );
    if (!hasTopBinding) {
      const entry = {
        agentId: name,
        match: { channel: "agentvault", accountId: name },
      };
      if (topBindings) {
        topBindings.push(entry);
      } else {
        freshConfig.bindings = [entry];
      }
      patched = true;
      console.log(`  Added top-level binding: ${name} → agentvault:${name}.`);
    }

    if (patched) {
      writeOpenClawConfig(home, freshConfig, name);
      console.log(`  httpPort set to ${port}.\n`);
    } else {
      console.log(`  Warning: Could not find account '${name}' in channels.agentvault.accounts.`);
      console.log(`  You may need to manually set httpPort: ${port} in openclaw.json.\n`);
    }
  } catch (err) {
    console.log(`  Warning: Failed to patch openclaw.json: ${(err as Error).message}\n`);
  }

  // ── Verify ──────────────────────────────────────────────────────────
  console.log("  Verifying agent...");

  try {
    const http = await import("node:http");
    await new Promise<void>((resolve) => {
      const req = http.get(`http://127.0.0.1:${port}/status`, { timeout: 3000 }, (res) => {
        console.log(`  Port ${port} responded (HTTP ${res.statusCode}).`);
        res.resume();
        resolve();
      });
      req.on("error", () => {
        console.log(`  Port ${port} not yet responding — gateway may need a moment.`);
        resolve();
      });
      req.on("timeout", () => {
        req.destroy();
        console.log(`  Port ${port} timed out — check gateway logs.`);
        resolve();
      });
    });
  } catch {
    console.log("  Verification skipped.");
  }

  // macOS: check for stale plist after setup
  if (process.platform === "darwin") {
    const { validatePlist } = await import("./doctor.js");
    const plistResult = validatePlist();
    if (plistResult.status === "stale") {
      console.log(`\n  ⚠️  macOS LaunchAgent plist has stale paths.
  Run to diagnose and fix: npx @agentvault/agentvault doctor\n`);
    }
  }

  // ── Summary ────────────────────────────────────────────────────────
  console.log(`
═══════════════════════════════════════════════
  Agent '${name}' created successfully!
═══════════════════════════════════════════════

  Workspace:  ${workspaceDir}
  DataDir:    ${dataDir}
  HTTP Port:  ${port}

  Next steps:
  1. Edit ${workspaceDir}/SOUL.md — give ${name} a personality
  2. Edit ${workspaceDir}/IDENTITY.md — set name, emoji, vibe
  3. Approve the agent in the AgentVault app
`);
}
